import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Autocomplete,
  Chip,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { IWorkflowInput } from "@/interfaces/workflow.interface";
import { IWorkflowStep } from "@/interfaces/workflow-step.interface";
import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";
import { IWorkflowKeyWordRule } from "@/types/workflow-keywords.types";
import { validateWorkflowKeyWordRules } from "@/utils/workflow-keywords";

type WorkflowKeyWordsSectionProps = {
  messages: WorkflowEditDialogMessages;
  workflowSteps?: IWorkflowStep[];
};

type WorkflowSettingsFormValues = IWorkflowInput & {
  workflowKeyWordsRules?: IWorkflowKeyWordRule[];
  workflowKeyWordDrafts?: Record<number, string>;
};

export default function WorkflowKeyWordsSection({
  messages,
  workflowSteps = [],
}: WorkflowKeyWordsSectionProps) {
  const { values, setFieldValue, errors } = useFormikContext<WorkflowSettingsFormValues>();
  const rules = values.workflowKeyWordsRules || [];
  const keywordDrafts = values.workflowKeyWordDrafts || {};
  const stepOptions = workflowSteps.map((step) => step.stepName).filter(Boolean);
  const { incompleteRuleIndexes } = validateWorkflowKeyWordRules(rules, keywordDrafts);

  const updateRules = (nextRules: IWorkflowKeyWordRule[]) => {
    setFieldValue("workflowKeyWordsRules", nextRules);
  };

  const updateKeywordDraft = (index: number, draft: string) => {
    setFieldValue("workflowKeyWordDrafts", { ...keywordDrafts, [index]: draft });
  };

  const clearKeywordDraft = (index: number) => {
    const nextDrafts = { ...keywordDrafts };
    delete nextDrafts[index];
    setFieldValue("workflowKeyWordDrafts", nextDrafts);
  };

  const handleAddRule = () => {
    updateRules([...rules, { keywords: [], destination: "" }]);
  };

  const handleRemoveRule = (index: number) => {
    updateRules(rules.filter((_, ruleIndex) => ruleIndex !== index));
    const nextDrafts = { ...keywordDrafts };
    delete nextDrafts[index];
    setFieldValue("workflowKeyWordDrafts", nextDrafts);
  };

  const handleDestinationChange = (index: number, destination: string) => {
    const nextRules = [...rules];
    nextRules[index] = { ...nextRules[index], destination };
    updateRules(nextRules);
  };

  const handleAddKeyword = (index: number) => {
    const keyword = (keywordDrafts[index] || "").trim();
    if (!keyword) {
      return;
    }

    const currentRule = rules[index];
    if (!currentRule || currentRule.keywords.includes(keyword)) {
      clearKeywordDraft(index);
      return;
    }

    const nextRules = [...rules];
    nextRules[index] = {
      ...currentRule,
      keywords: [...currentRule.keywords, keyword],
    };
    updateRules(nextRules);
    clearKeywordDraft(index);
  };

  const handleRemoveKeyword = (ruleIndex: number, keyword: string) => {
    const nextRules = [...rules];
    nextRules[ruleIndex] = {
      ...nextRules[ruleIndex],
      keywords: nextRules[ruleIndex].keywords.filter((item) => item !== keyword),
    };
    updateRules(nextRules);
  };

  return (
    <MDBox>
      <MDTypography variant="body2" color="text" mb={2}>
        {messages.workflowKeyWordsHint}
      </MDTypography>

      {rules.map((rule, index) => (
        <MDBox
          key={`workflow-keyword-rule-${index}`}
          mb={2}
          p={2}
          sx={{
            border: "1px solid",
            borderColor: incompleteRuleIndexes.includes(index) ? "error.main" : "divider",
            borderRadius: 1,
          }}
        >
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="subtitle2" fontWeight="medium">
              {messages.workflowKeyWordsRuleLabel} {index + 1}
            </MDTypography>
            <IconButton
              aria-label={messages.workflowKeyWordsRemoveRuleLabel}
              onClick={() => handleRemoveRule(index)}
              size="small"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </MDBox>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                freeSolo
                options={[]}
                value={keywordDrafts[index] || ""}
                onInputChange={(_, newValue) => updateKeywordDraft(index, newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={messages.workflowKeyWordsKeywordsLabel}
                    fullWidth
                    onBlur={() => handleAddKeyword(index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleAddKeyword(index);
                      }
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleAddKeyword(index)}
                              disabled={!(keywordDrafts[index] || "").trim()}
                              edge="end"
                            >
                              <AddIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
              <MDBox mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                {rule.keywords.map((keyword) => (
                  <Chip
                    key={`${index}-${keyword}`}
                    label={keyword}
                    size="small"
                    deleteIcon={<HighlightOffIcon />}
                    onDelete={() => handleRemoveKeyword(index, keyword)}
                  />
                ))}
              </MDBox>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={stepOptions}
                value={rule.destination || ""}
                onChange={(_, newValue) => handleDestinationChange(index, newValue || "")}
                onInputChange={(_, newInputValue) => handleDestinationChange(index, newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={messages.workflowKeyWordsDestinationLabel}
                    fullWidth
                  />
                )}
                freeSolo
              />
            </Grid>
          </Grid>
        </MDBox>
      ))}

      <MDButton variant="outlined" color="dark" onClick={handleAddRule}>
        {messages.workflowKeyWordsAddRuleLabel}
      </MDButton>

      {errors.workflowKeyWords && (
        <FormHelperText error sx={{ mt: 1 }}>
          {String(errors.workflowKeyWords)}
        </FormHelperText>
      )}
    </MDBox>
  );
}
